/**
 * useOptimisticMessages — React 19 optimistic UI for chat messages
 *
 * Adapted from bachata-nation-next useOptimisticMessages.ts.
 * Uses useOptimistic for instant message display before server confirmation.
 */

import { useOptimistic, useCallback, useRef, useTransition } from 'react';
import { useAuthStore } from '@/features/auth';
import type { ChatMessage } from '../types';

type OptimisticAction =
  | { type: 'add'; message: ChatMessage }
  | { type: 'confirm'; tempId: string; realMessage?: ChatMessage }
  | { type: 'fail'; tempId: string; error: string }
  | { type: 'retry'; tempId: string }
  | { type: 'remove'; tempId: string };

function optimisticReducer(messages: ChatMessage[], action: OptimisticAction): ChatMessage[] {
  switch (action.type) {
    case 'add':
      return [...messages, action.message];

    case 'confirm': {
      if (action.realMessage) {
        return messages.map(m =>
          m.id === action.tempId ? { ...action.realMessage!, isPending: false, isFailed: false } : m
        );
      }
      return messages.map(m =>
        m.id === action.tempId ? { ...m, isPending: false, isFailed: false } : m
      );
    }

    case 'fail':
      return messages.map(m =>
        m.id === action.tempId ? { ...m, isPending: false, isFailed: true } : m
      );

    case 'retry':
      return messages.map(m =>
        m.id === action.tempId ? { ...m, isPending: true, isFailed: false } : m
      );

    case 'remove':
      return messages.filter(m => m.id !== action.tempId);

    default:
      return messages;
  }
}

interface UseOptimisticMessagesOptions {
  messages: ChatMessage[];
  conversationId: string;
  sendMessage: (params: {
    content: string;
    type?: string;
    replyToId?: string;
  }) => Promise<ChatMessage | void>;
}

export function useOptimisticMessages({
  messages,
  conversationId,
  sendMessage,
}: UseOptimisticMessagesOptions) {
  const user = useAuthStore((s) => s.user);
  const [optimisticMessages, updateOptimistic] = useOptimistic(messages, optimisticReducer);
  const [isPending, startTransition] = useTransition();
  const pendingMessagesRef = useRef(new Map<string, { content: string; type?: string; replyToId?: string; replyTo?: ChatMessage['replyTo'] }>());

  const sendOptimistic = useCallback(
    async (params: { content: string; type?: string; replyToId?: string; replyTo?: ChatMessage['replyTo'] }) => {
      if (!user) return;

      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const optimisticMessage: ChatMessage = {
        id: tempId,
        conversationId,
        senderId: user.id,
        sequenceId: -1,
        type: (params.type as ChatMessage['type']) || 'text',
        content: params.content,
        mediaUrl: null,
        mediaThumbnail: null,
        mediaMetadata: null,
        replyTo: params.replyTo || null,
        isEdited: false,
        editedAt: null,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        sender: {
          id: user.id,
          name: [user.firstName, user.lastName].filter(Boolean).join(' ') || 'User',
          avatar: user.avatarUrl,
        },
        reactions: {},
        isStarred: false,
        isPending: true,
        isFailed: false,
      };

      pendingMessagesRef.current.set(tempId, params);

      startTransition(() => {
        updateOptimistic({ type: 'add', message: optimisticMessage });
      });

      try {
        const result = await sendMessage(params);

        startTransition(() => {
          updateOptimistic({
            type: 'confirm',
            tempId,
            realMessage: result || undefined,
          });
        });

        pendingMessagesRef.current.delete(tempId);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
        startTransition(() => {
          updateOptimistic({ type: 'fail', tempId, error: errorMessage });
        });
      }
    },
    [user, conversationId, sendMessage, startTransition, updateOptimistic]
  );

  const markFailed = useCallback(
    (tempId: string, error: string) => {
      startTransition(() => {
        updateOptimistic({ type: 'fail', tempId, error });
      });
    },
    [startTransition, updateOptimistic]
  );

  const retrySend = useCallback(
    async (tempId: string) => {
      const params = pendingMessagesRef.current.get(tempId);
      if (!params) return;

      startTransition(() => {
        updateOptimistic({ type: 'retry', tempId });
      });

      try {
        const result = await sendMessage(params);
        startTransition(() => {
          updateOptimistic({ type: 'confirm', tempId, realMessage: result || undefined });
        });
        pendingMessagesRef.current.delete(tempId);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
        startTransition(() => {
          updateOptimistic({ type: 'fail', tempId, error: errorMessage });
        });
      }
    },
    [sendMessage, startTransition, updateOptimistic]
  );

  const removeOptimistic = useCallback(
    (tempId: string) => {
      pendingMessagesRef.current.delete(tempId);
      startTransition(() => {
        updateOptimistic({ type: 'remove', tempId });
      });
    },
    [startTransition, updateOptimistic]
  );

  return {
    optimisticMessages,
    isPending,
    sendOptimistic,
    markFailed,
    retrySend,
    removeOptimistic,
  };
}

export default useOptimisticMessages;
